import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ShoppingCart, Star, Plus, Minus, ChefHat, Phone, MapPin, X, Send } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CustomerMenuPage() {
  const { branchId } = useParams();
  const [searchParams] = useSearchParams();
  const table = searchParams.get('table') || '';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [activeCategory, setActiveCategory] = useState('');
  const [showReview, setShowReview] = useState(false);
  const [reviewForm, setReviewForm] = useState({ customer_name: '', stars: 5, comment: '' });
  const [submittingReview, setSubmittingReview] = useState(false);

  useEffect(() => {
    const fetchMenu = async () => {
      try {
        const { data: d } = await axios.get(`${API}/api/public/qr-menu/${branchId}?table=${table}`);
        setData(d);
        const cats = Object.keys(d.categories || {});
        if (cats.length > 0) setActiveCategory(cats[0]);
      } catch { /* error */ }
      setLoading(false);
    };
    fetchMenu();
  }, [branchId, table]);

  const addToCart = (item) => {
    setCart(prev => {
      const existing = prev.find(c => c.id === item.id);
      if (existing) return prev.map(c => c.id === item.id ? { ...c, qty: c.qty + 1 } : c);
      return [...prev, { ...item, qty: 1 }];
    });
  };

  const updateQty = (itemId, delta) => {
    setCart(prev => prev.map(c => c.id === itemId ? { ...c, qty: Math.max(0, c.qty + delta) } : c).filter(c => c.qty > 0));
  };

  const cartTotal = cart.reduce((sum, c) => sum + c.price * c.qty, 0);
  const cartCount = cart.reduce((sum, c) => sum + c.qty, 0);

  const submitReview = async () => {
    if (!reviewForm.customer_name || !reviewForm.stars) return;
    setSubmittingReview(true);
    try {
      await axios.post(`${API}/api/public/reviews`, {
        branch_id: branchId,
        customer_name: reviewForm.customer_name,
        stars: reviewForm.stars,
        comment: reviewForm.comment,
      });
      setShowReview(false);
      setReviewForm({ customer_name: '', stars: 5, comment: '' });
    } catch { /* silent */ }
    setSubmittingReview(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#FFF9F0' }}>
        <div className="w-10 h-10 border-3 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#FFF9F0' }}>
        <p className="text-gray-500">القائمة غير متاحة</p>
      </div>
    );
  }

  const branch = data.branch || {};
  const settings = data.settings || {};
  const categories = data.categories || {};
  const catKeys = Object.keys(categories);
  const themeColor = settings.theme_color || '#D97706';

  return (
    <div className="min-h-screen pb-24" style={{ background: '#FFF9F0', fontFamily: 'Cairo, Tajawal, sans-serif' }} dir="rtl" data-testid="customer-menu-page">
      {/* Header */}
      <div className="sticky top-0 z-30" style={{ background: `linear-gradient(135deg, ${themeColor}, ${themeColor}dd)` }}>
        <div className="px-4 py-5 text-white">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center backdrop-blur-sm">
              <ChefHat className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold">{branch.name || 'المطعم'}</h1>
              {table && <p className="text-sm text-white/80">طاولة #{table}</p>}
            </div>
          </div>
          {branch.address && (
            <div className="flex items-center gap-1.5 mt-2 text-xs text-white/70">
              <MapPin className="w-3 h-3" /> {branch.address}
            </div>
          )}
        </div>

        {/* Category Tabs */}
        {catKeys.length > 1 && (
          <div className="flex gap-2 px-4 pb-3 overflow-x-auto no-scrollbar">
            {catKeys.map(cat => (
              <button key={cat} onClick={() => setActiveCategory(cat)} data-testid={`cat-tab-${cat}`}
                className="px-4 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all"
                style={{
                  background: activeCategory === cat ? 'white' : 'rgba(255,255,255,0.15)',
                  color: activeCategory === cat ? themeColor : 'rgba(255,255,255,0.85)',
                }}>
                {cat}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Menu Items */}
      <div className="px-4 py-4 space-y-3">
        {(categories[activeCategory] || []).map(item => {
          const inCart = cart.find(c => c.id === item.id);
          return (
            <div key={item.id} data-testid={`menu-item-${item.id}`}
              className="bg-white rounded-xl p-3 shadow-sm border border-gray-100 flex gap-3">
              {item.image_url && (
                <img src={item.image_url} alt="" className="w-20 h-20 rounded-lg object-cover flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-sm text-gray-900">{item.name_ar || item.name}</h3>
                {item.description_ar && <p className="text-[11px] text-gray-500 mt-0.5 line-clamp-2">{item.description_ar}</p>}
                <div className="flex items-center justify-between mt-2">
                  {(settings.show_prices !== false) && (
                    <span className="font-bold text-sm" style={{ color: themeColor }}>{item.price} ر.س</span>
                  )}
                  {inCart ? (
                    <div className="flex items-center gap-2 bg-gray-100 rounded-full px-1">
                      <button onClick={() => updateQty(item.id, -1)} className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-gray-200">
                        <Minus className="w-3.5 h-3.5" />
                      </button>
                      <span className="text-sm font-bold w-5 text-center">{inCart.qty}</span>
                      <button onClick={() => updateQty(item.id, 1)} className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-gray-200">
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : (settings.allow_ordering !== false) && (
                    <button onClick={() => addToCart(item)} data-testid={`add-item-${item.id}`}
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white transition-transform hover:scale-105"
                      style={{ background: themeColor }}>
                      <Plus className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Review Button */}
      <div className="px-4 mt-4">
        <button onClick={() => setShowReview(true)} data-testid="write-review-btn"
          className="w-full py-3 rounded-xl border-2 border-dashed text-sm font-semibold transition-colors"
          style={{ borderColor: `${themeColor}40`, color: themeColor }}>
          <Star className="w-4 h-4 inline-block me-1.5" />
          قيّم تجربتك
        </button>
      </div>

      {/* Floating Cart */}
      {cartCount > 0 && (
        <div className="fixed bottom-4 inset-x-4 z-40" data-testid="floating-cart">
          <button onClick={() => setShowCart(true)}
            className="w-full py-3.5 rounded-2xl text-white font-bold text-sm shadow-xl flex items-center justify-between px-5 transition-transform active:scale-[0.98]"
            style={{ background: themeColor }}>
            <div className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5" />
              <span>عرض السلة ({cartCount})</span>
            </div>
            <span>{cartTotal.toFixed(2)} ر.س</span>
          </button>
        </div>
      )}

      {/* Cart Modal */}
      {showCart && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end" onClick={() => setShowCart(false)}>
          <div className="w-full bg-white rounded-t-2xl max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="cart-modal">
            <div className="sticky top-0 bg-white px-4 py-3 border-b flex items-center justify-between">
              <h3 className="font-bold text-base">السلة</h3>
              <button onClick={() => setShowCart(false)} className="p-1"><X className="w-5 h-5" /></button>
            </div>
            <div className="px-4 py-3 space-y-3">
              {cart.map(c => (
                <div key={c.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                  <div>
                    <p className="text-sm font-semibold">{c.name_ar || c.name}</p>
                    <p className="text-xs text-gray-500">{c.price} ر.س × {c.qty}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => updateQty(c.id, -1)} className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center">
                      <Minus className="w-3.5 h-3.5" />
                    </button>
                    <span className="font-bold text-sm w-5 text-center">{c.qty}</span>
                    <button onClick={() => updateQty(c.id, 1)} className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center">
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className="px-4 py-3 border-t">
              <div className="flex justify-between text-base font-bold mb-3">
                <span>المجموع</span>
                <span style={{ color: themeColor }}>{cartTotal.toFixed(2)} ر.س</span>
              </div>
              <button className="w-full py-3 rounded-xl text-white font-bold text-sm" style={{ background: themeColor }}
                data-testid="confirm-order-btn" onClick={() => { toast.success('تم إرسال الطلب!'); setCart([]); setShowCart(false); }}>
                تأكيد الطلب {table ? `- طاولة #${table}` : ''}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Review Modal */}
      {showReview && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowReview(false)}>
          <div className="bg-white rounded-2xl w-full max-w-sm p-5" onClick={e => e.stopPropagation()} data-testid="review-modal">
            <h3 className="font-bold text-lg text-center mb-4">قيّم تجربتك</h3>
            <div className="flex justify-center gap-2 mb-4">
              {[1, 2, 3, 4, 5].map(s => (
                <button key={s} onClick={() => setReviewForm({ ...reviewForm, stars: s })} data-testid={`review-star-${s}`}>
                  <Star className="w-8 h-8 transition-colors" fill={s <= reviewForm.stars ? '#FBBF24' : 'none'} stroke={s <= reviewForm.stars ? '#FBBF24' : '#D1D5DB'} />
                </button>
              ))}
            </div>
            <input value={reviewForm.customer_name} onChange={e => setReviewForm({ ...reviewForm, customer_name: e.target.value })}
              placeholder="اسمك" className="w-full px-4 py-2.5 rounded-xl border text-sm mb-3" data-testid="review-name-input" />
            <textarea value={reviewForm.comment} onChange={e => setReviewForm({ ...reviewForm, comment: e.target.value })}
              placeholder="اكتب تعليقك (اختياري)" rows={3} className="w-full px-4 py-2.5 rounded-xl border text-sm mb-4 resize-none" data-testid="review-comment-input" />
            <div className="flex gap-2">
              <button onClick={() => setShowReview(false)} className="flex-1 py-2.5 rounded-xl border text-sm font-semibold">إلغاء</button>
              <button onClick={submitReview} disabled={submittingReview || !reviewForm.customer_name}
                className="flex-1 py-2.5 rounded-xl text-white text-sm font-semibold disabled:opacity-50"
                style={{ background: themeColor }} data-testid="submit-review-btn">
                {submittingReview ? '...' : 'إرسال التقييم'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// end of file
